import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStudentPortalContext } from "@/contexts/StudentPortalContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, History, QrCode, Wifi } from "lucide-react";
import QrCodeScannerModal from "@/components/QrCodeScannerModal";
import StudentVideoLessonPlayer from "@/components/StudentVideoLessonPlayer";
import { tr, CHECKIN_METHOD_LABEL } from "@/lib/labels";
import { LessonMeta, PortalEmpty, PortalLoading, PortalPageHeader } from "./StudentPortalParts";

function CheckinActions({ lesson, attendance, perms, onChanged, onScan }) {
  const [busy, setBusy] = useState(false);
  const already = attendance.find((row) => row.lesson_id === lesson.id);
  if (already) return <div className="mt-4 p-3 rounded-xl bg-[#E6F4EA] text-[#2F855A] text-sm inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Presença registrada ({tr(CHECKIN_METHOD_LABEL, already.checkin_method)})</div>;

  const allowed = perms.find((row) => row.lesson_id === lesson.id && row.online_checkin_allowed);
  const showQr = lesson.status === "OPEN" && ["PRESENTIAL", "HYBRID"].includes(lesson.lesson_type);
  const canOnline = lesson.status === "OPEN" && (lesson.lesson_type === "ONLINE" || !!allowed);
  if (!showQr && !canOnline) return null;

  const online = async () => {
    setBusy(true);
    try { await api.post(`/student/portal/lessons/${lesson.id}/checkin-online`); toast.success("Presença online registrada!"); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return <div className="mt-4 grid sm:grid-cols-2 gap-2">{showQr && <button onClick={() => onScan(lesson)} className="doxa-btn-primary py-3 inline-flex items-center justify-center gap-2"><QrCode className="w-4 h-4" /> Registrar por QR</button>}{canOnline && <button onClick={online} disabled={busy} className={`${showQr ? "rounded-xl bg-[#F3EFE7] text-[#4A4640]" : "doxa-btn-primary"} py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50`}><Wifi className="w-4 h-4" /> {busy ? "Registrando…" : "Registrar online"}</button>}</div>;
}

function LessonCard({ lesson, attendance, perms, onChanged, onScan, featured = false }) {
  return (
    <article className={`doxa-card ${featured ? "p-5 sm:p-6 border-[#F3C7B5]" : "p-4 sm:p-5"}`}>
      {featured && <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#F75A1B] mb-2">Acontecendo agora</p>}
      <h2 className={`font-display font-bold ${featured ? "text-xl" : "text-base"}`}>{lesson.title}</h2>
      <LessonMeta lesson={lesson} />
      {lesson.status === "OPEN" && lesson.video_url && <StudentVideoLessonPlayer lesson={lesson} />}
      {lesson.status === "OPEN" && <CheckinActions lesson={lesson} attendance={attendance} perms={perms} onChanged={onChanged} onScan={onScan} />}
    </article>
  );
}

export default function StudentLessons() {
  const { loading: contextLoading, selectedClassId } = useStudentPortalContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanLesson, setScanLesson] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    if (!selectedClassId) { setData(null); setLoading(false); return; }
    setLoading(true);
    try { const { data: next } = await api.get("/student/portal/lessons", { params: { class_group_id: selectedClassId } }); setData(next); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [selectedClassId]);

  const groups = useMemo(() => {
    const lessons = data?.lessons || [];
    return {
      open: lessons.filter((row) => row.status === "OPEN"),
      scheduled: lessons.filter((row) => row.status === "SCHEDULED"),
      history: lessons.filter((row) => row.status === "CLOSED").slice().reverse(),
    };
  }, [data]);

  const onDetected = (path) => { setScanLesson(null); navigate(path); };

  if (contextLoading || loading) return <PortalLoading />;
  if (!selectedClassId) return <PortalEmpty icon={CalendarDays} title="Nenhuma turma ativa" />;

  return (
    <div data-testid="student-lessons-v2">
      <PortalPageHeader eyebrow="Sua jornada" title="Aulas" description="Presença, check-in e vídeo ficam juntos no contexto de cada aula." />
      <QrCodeScannerModal open={!!scanLesson} expectedLessonId={scanLesson?.id} onClose={() => setScanLesson(null)} onDetected={onDetected} />

      {groups.open.length > 0 && <section className="space-y-3 mb-7">{groups.open.map((lesson) => <LessonCard key={lesson.id} lesson={lesson} attendance={data.attendance || []} perms={data.online_perms || []} onChanged={load} onScan={setScanLesson} featured />)}</section>}

      <section className="mb-7">
        <h2 className="font-display text-lg font-bold mb-3 inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#F75A1B]" /> Próximas aulas</h2>
        {groups.scheduled.length ? <div className="grid md:grid-cols-2 gap-3">{groups.scheduled.map((lesson) => <LessonCard key={lesson.id} lesson={lesson} attendance={data.attendance || []} perms={data.online_perms || []} onChanged={load} onScan={setScanLesson} />)}</div> : <p className="text-sm text-[#6B655E]">Nenhuma aula agendada no momento.</p>}
      </section>

      <section>
        <h2 className="font-display text-lg font-bold mb-3 inline-flex items-center gap-2"><History className="w-4 h-4 text-[#F75A1B]" /> Aulas anteriores</h2>
        {groups.history.length ? <div className="grid md:grid-cols-2 gap-3">{groups.history.map((lesson) => <LessonCard key={lesson.id} lesson={lesson} attendance={data.attendance || []} perms={data.online_perms || []} onChanged={load} onScan={setScanLesson} />)}</div> : <p className="text-sm text-[#6B655E]">Nenhuma aula encerrada ainda.</p>}
      </section>
    </div>
  );
}
